/**
 * Which theme the app renders: one pure function, so the rule is a truth table and
 * not something each screen works out.
 *
 *   web                      → light   (D1: the legacy web app is not themed)
 *   the dark-mode flag off   → light   (the flag carries the whole rollout: with it
 *                                        off, a half-migrated app is still all light)
 *   preference light / dark  → that
 *   preference system        → the phone's scheme; unknown counts as light
 *
 * Plan: docs/plans/DARK_MODE_SPRINT.md (the engine; gate G4).
 */

export type Scheme = 'light' | 'dark';

/** What the athlete chose in Account › Appearance. */
export type Preference = 'system' | 'light' | 'dark';

export const PREFERENCES: readonly Preference[] = ['system', 'light', 'dark'];

/** A device with no saved choice follows the phone (D2 / D17). */
export const DEFAULT_PREFERENCE: Preference = 'system';

export interface ResolveSchemeInput {
  /** `Platform.OS`. */
  platform: string;
  /** The per-user `dark_mode_enabled` flag, already resolved to a boolean. */
  enabled: boolean;
  preference: Preference;
  /** What the phone reports. `null` / `undefined` when the platform does not say. */
  system: Scheme | null | undefined;
}

export function resolveScheme({ platform, enabled, preference, system }: ResolveSchemeInput): Scheme {
  if (platform === 'web') return 'light';
  if (!enabled) return 'light';
  if (preference === 'light' || preference === 'dark') return preference;
  return system === 'dark' ? 'dark' : 'light';
}

/** True when `raw` is one of the three stored spellings. */
export function isPreference(raw: unknown): raw is Preference {
  return raw === 'system' || raw === 'light' || raw === 'dark';
}

/**
 * What the app passes to `Appearance.setColorScheme` so that the UI the phone draws
 * itself (keyboard, pickers, share sheets) matches the app (D3):
 *
 *   an explicit Light or Dark → force it;
 *   System                    → `null`, which React Native sends as "unspecified",
 *                               handing the choice back to the phone.
 *
 * In React Native 0.81 a forced scheme makes `useColorScheme()` return the forced
 * value, not the phone's. So the provider only reads the phone's scheme while
 * nothing is forced, which is exactly when the preference is System.
 */
export function appearanceOverrideFor(preference: Preference): Scheme | null {
  return preference === 'system' ? null : preference;
}
